import office from '@/taskpane/officejs/office-wrapper'
import officeApiWrapper from '@/taskpane/common/office-api-wrapper'
import AttachmentChunk from '@/taskpane/services/models/attachment-chunk'
import CallbackArgs, { defaultCallbackArgs } from '../messages/callback-args'
import MessageArgs from '../messages/message-args'
import sendMessageToSidebar from '../messages/send-message-to-sidebar'
import AttachmentContents from '../models/attachment-contents'
import AttachmentInfo from '../models/attachment-info'
import CirrusApi from '@/taskpane/services/cirrus-api'
import AttachmentRequest from '@/taskpane/services/models/attachment-request'

interface RetrieveAttachmentsArgs extends MessageArgs {
  attachments: AttachmentInfo[]
  includeEmailAsFile?: boolean
}

interface RetrieveAttachmentsCallbackArgs extends CallbackArgs {
  attachments: AttachmentContents[]
}

export default async function retrieveAttachmentsHandler(data: RetrieveAttachmentsArgs) {
  const callbackArgs = defaultCallbackArgs() as RetrieveAttachmentsCallbackArgs
  const selectedAttachments = data.attachments.filter((attachment) => attachment.selected)

  try {
    const attachmentContents = selectedAttachments.length ? await processAttachment(selectedAttachments) : []

    if (data.includeEmailAsFile) {
      attachmentContents.unshift(await getEmailAsEml())
    }

    callbackArgs.attachments = await uploadAttachments(attachmentContents)
    sendMessageToSidebar('RetrieveAttachmentsCallback', callbackArgs)
  } catch (error) {
    callbackArgs.errorCode = 500
    callbackArgs.errorMessage = error || 'Failed to retrieve attachments'
    sendMessageToSidebar('RetrieveAttachmentsCallback', callbackArgs)
  }
}

async function getEmailAsEml(): Promise<AttachmentContents> {
  try {
    return await office.attachments.getEmailAsEml()
  } catch {
    throw 'Unable to save the email as a file.'
  }
}

async function processAttachment(attachments: AttachmentInfo[]): Promise<AttachmentContents[]> {
  try {
    const attachmentPromises: Promise<AttachmentContents>[] = []

    attachments.forEach((attachment) => {
      attachmentPromises.push(getAttachmentContents(attachment))
    })

    const attachmentContents = await Promise.all(attachmentPromises)
    return attachmentContents
  } catch (error) {
    return getAttachmentContentsUsingEws(attachments)
  }
}

async function getAttachmentContents(attachment: AttachmentInfo): Promise<AttachmentContents> {
  try {
    return await office.attachments.getContents(attachment)
  } catch {
    return getAttachmentContentsUsingApi(attachment)
  }
}

async function getAttachmentContentsUsingApi(attachment: AttachmentInfo): Promise<AttachmentContents> {
  if (officeApiWrapper.isAvailable()) {
    const id = (await office.id()) || (await office.save())
    if (office.mode.isAppointment()) {
      return officeApiWrapper.getEventAttachment(id, attachment)
    } else if (office.mode.isMessage()) {
      return officeApiWrapper.getMailAttachment(id, attachment)
    }
  } else {
    throw 'Unable to retrieve attachment'
  }
}

async function getAttachmentContentsUsingEws(attachments: AttachmentInfo[]): Promise<AttachmentContents[]> {
  const ewsToken = await office.ews.token()
  if (ewsToken && office.ews.url) {
    const ewsRequest: AttachmentRequest = {
      ewsUrl: office.ews.url(),
      attachmentToken: ewsToken,
      attachmentInfos: attachments,
    }
    return CirrusApi.getAttachmentContents(ewsRequest)
  } else {
    throw 'Unable to download attachment'
  }
}

async function uploadAttachments(attachments: AttachmentContents[]): Promise<AttachmentContents[]> {
  const chunkSize = 500 * 1000
  const uploadPromises: Promise<void>[] = []

  attachments.forEach((attachment, index) => {
    let chunkStartIndex = 0
    let chunkSequenceIndex = 1
    attachment.totalChunks = Math.ceil(attachment.contentsBase64.length / chunkSize)
    attachment.chunkId = Date.now() + '_' + Math.ceil(Math.random() * 10000000000) + '_' + (index + 1)

    do {
      const attachmentChunk: AttachmentChunk = {
        chunkId: attachment.chunkId + '',
        sequence: chunkSequenceIndex,
        chunk: attachment.contentsBase64.substring(chunkStartIndex, chunkStartIndex + chunkSize),
      }
      const uploadPromise = CirrusApi.uploadAttachmentChunk(attachmentChunk)

      chunkSequenceIndex++
      chunkStartIndex += chunkSize
      uploadPromises.push(uploadPromise)
    } while (chunkStartIndex < attachment.contentsBase64.length)

    delete attachment.contentsBase64
  })

  await Promise.all(uploadPromises)
  return attachments
}
